/**
 * Organization switcher dropdown component.
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function OrgSwitcher() {
  const { organizations, currentOrganization, switchOrganization } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (organizations.length === 0) {
    return null;
  }

  const handleSelect = (orgId: string) => {
    switchOrganization(orgId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-border
                   hover:bg-surface-tertiary transition-colors"
      >
        <div className="w-6 h-6 rounded-full bg-orbital-600 flex items-center justify-center">
          <span className="text-white text-xs font-medium">
            {currentOrganization?.name?.charAt(0).toUpperCase() || 'O'}
          </span>
        </div>
        <span className="text-sm font-medium text-content-primary max-w-[120px] truncate">
          {currentOrganization?.name || 'Select Organization'}
        </span>
        <svg
          className={`w-4 h-4 text-content-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-surface-primary rounded-lg shadow-lg border border-border py-1 z-50">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-medium text-content-tertiary uppercase tracking-wide">
              Your Organizations
            </p>
          </div>

          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSelect(org.id)}
              className={`w-full px-3 py-2 flex items-center space-x-3 hover:bg-surface-tertiary
                         transition-colors ${org.id === currentOrganization?.id ? 'bg-orbital-50 dark:bg-orbital-950' : ''}`}
            >
              <div className="w-8 h-8 rounded-full bg-orbital-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-medium">
                  {org.name?.charAt(0).toUpperCase() || 'O'}
                </span>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-content-primary">{org.name}</p>
                <p className="text-xs text-content-tertiary capitalize">{org.role}</p>
              </div>
              {org.id === currentOrganization?.id && (
                <svg className="w-5 h-5 text-orbital-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
